import { AGENCY_TIME_ZONE, formatRecordedCalendarDate, offerGroups, type OfferGroup, type OfferRow } from '@vigeo/shared';

/**
 * The words the Offers list says, kept apart from the component that lays them out (#259).
 *
 * The API sends facts — a group, some dates, the terms — and every sentence on the screen is
 * built here, so the wording Paul settles on changes in one file and the tests can read it
 * without rendering anything.
 */

export interface OfferGroupCopy {
  readonly id: OfferGroup;
  readonly title: string;
  /** The line under the group's title, straight from the approved frame. */
  readonly blurb: string;
}

export const offerGroupCopy: readonly OfferGroupCopy[] = [
  { id: 'to-prepare', title: 'To prepare', blurb: 'Interviewed and added. No offer yet.' },
  { id: 'drafts', title: 'Drafts', blurb: 'Saved but not reviewed. A draft can’t be sent until it’s reviewed.' },
  { id: 'ready-to-send', title: 'Ready to send', blurb: 'Reviewed and waiting to go out to the candidate.' },
  {
    id: 'out-for-signature',
    title: 'Out for signature',
    blurb: 'Sent to the candidate’s portal, with the link by email. They sign by typing their full name before the Accept by date.',
  },
  { id: 'signed', title: 'Signed', blurb: 'Accepted and filed. The candidate can move to Onboarding.' },
  /**
   * NOT IN THE APPROVED FRAME, and appended last so nothing the frame draws moves (#301).
   *
   * The frame has no wording for it because it had no group for it: an expired offer sat under "Out
   * for signature" for ever, under a blurb that says the candidate signs "before the Accept by date".
   * The portal has always refused a signature after that date, so this group names a state the system
   * already had. The badge reuses the existing "Past the accept by date" wording rather than inventing
   * a second phrasing of it.
   */
  { id: 'expired', title: 'Expired', blurb: 'Sent, and the Accept by date passed without a signature. Draft new terms to try again.' },
];

if (offerGroupCopy.length !== offerGroups.length) throw new Error('every offer group needs its wording');

/**
 * The words for a candidate whose records could not be read (#299).
 *
 * NOT A SIXTH GROUP. `offerGroups` is what the filter pills add up and what `offerGroupCounts`
 * counts; a candidate whose group is unknown belongs to none of them, and adding one would put a
 * permanent "Couldn’t be read (0)" pill on a healthy screen. This is a band of its own, drawn only
 * when there is something in it.
 *
 * SAYS WHAT IS MISSING AND WHAT IS NOT. The person is still in hiring and their record still opens;
 * the only unknown is where their offer stands. "Nothing has been lost" is there because the first
 * thing anyone reads a row like this as is "the offer is gone".
 */
export const offersUnavailableCopy = {
  title: 'Couldn’t be read',
  blurb:
    'These candidates are in hiring, but something in their records could not be read, so where their offer stands is unknown. Nothing has been lost — open the candidate to see their record.',
  summary: 'Offer status unavailable',
  detail: 'Something in this candidate’s records could not be read.',
  action: 'Open record',
} as const;

/**
 * What the list says when the roster is larger than one page of it (#300).
 *
 * The bound is the API's (`CANDIDATE_BOUND`) and arrives on the response, so this sentence never
 * hardcodes the number. It names the ORDER as well as the ceiling: which candidates were left out
 * is the reader's first question, and "waiting longest first" is the answer the query guarantees.
 */
export function showingFirstCandidates(counted: number): string {
  return `Showing the first ${counted} candidates, the ones added longest ago. There are more.`;
}

/** A day and a time, as the frame writes them: "Sep 17, 5:00 PM". */
export function dayAndTime(instant: string, locale = 'en-US'): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: AGENCY_TIME_ZONE,
  }).format(new Date(instant));
}

/** A day on its own: "Sep 10". */
export function day(instant: string, locale = 'en-US'): string {
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: AGENCY_TIME_ZONE }).format(new Date(instant));
}

/**
 * "LPN · W-2 · Full-time", or what to say when there are no typed terms to show.
 *
 * A RECORDED offer has none — `existing-offer` holds the signed copy and a note, not a position or
 * a classification — so the row must not fall through to "No offer yet" beside a Signed badge,
 * which is what it did. Whether an offer exists is the group's answer, never the terms'.
 */
export function offerSummary(row: OfferRow): string {
  if (row.offer !== null) {
    return [row.offer.position, row.offer.employmentClassification === 'w2' ? 'W-2' : '1099', row.offer.employmentType].join(' · ');
  }
  return row.group === 'signed' ? 'Signed offer on file' : 'No offer yet';
}

/** The quieter second line under the summary. */
export function offerDetail(row: OfferRow): string {
  // Signed first: a recorded offer reaches here with no terms, and its second line is the whole
  // explanation of where the signature came from.
  if (row.group === 'signed') {
    // AN OFFER IS STILL OUT. `record-existing-offer` has no guard against a live portal offer —
    // `issue-offer` does — so a paper signature can be recorded while one is still in the
    // candidate's portal, where they can still open and sign it. Saying only "Signed" would make
    // that offer disappear from the one screen meant to show every offer in flight (#275).
    // Where the signature came from is `signedVia`'s to say, not this branch's. Saying "on paper"
    // unconditionally mislabelled a PORTAL signature whenever another offer was still out — reachable
    // by withdrawing a signed portal offer and issuing replacement terms, since `issue-offer` only
    // refuses a second offer while one is still `issued`. The warning is the point; the provenance
    // was collateral.
    if (row.liveOffers > 0) {
      return row.signedVia === 'recorded'
        ? 'Signed on paper — an offer is still out for signature'
        : 'Signed in the portal — another offer is still out for signature';
    }
    return row.signedVia === 'recorded' ? 'Recorded from a signed paper copy' : 'Signed in the portal';
  }
  /*
   * "ONE OF n", not "showing the soonest of n" (#297). While the list was one row per PERSON this
   * row stood in for every offer that candidate had out, and saying so was the only way the others
   * were acknowledged at all. Every offer has its own row now, so nothing is being stood in for —
   * the honest thing to say is that this candidate is holding more than one live link, which is
   * worth knowing and is no longer the same claim.
   */
  if (row.group === 'out-for-signature' && row.liveOffers > 1) {
    return `One of ${row.liveOffers} offers out for signature`;
  }
  // The badge says the date has passed; this says what it means. Not the Accept-by date again — that
  // is already in the Key date column.
  if (row.group === 'expired') return 'Nobody signed it. Draft new terms to try again.';
  if (row.offer === null) return `Added ${day(row.addedAt)} after the interview`;
  return `Proposed start ${formatRecordedCalendarDate(row.offer.proposedStartDate)}`;
}

export type BadgeTone = 'neutral' | 'brand' | 'good' | 'warning';

/**
 * The status badge.
 *
 * The countdown is the one automation on this screen (Julio, 2026-09-22): inside a week of the
 * deadline it says how long is left and turns amber, and it is the only place a number is
 * calculated rather than read. Everything else names the state the record is in.
 *
 * STRICTLY INSIDE, which is the whole difference between a warning and wallpaper. The agency's
 * default response window is a week (`defaultOfferResponseDays`), so a freshly issued offer is
 * exactly `COUNTDOWN_WINDOW_DAYS` away: at `<=` every offer would be amber from the moment it
 * went out, the quiet state would never appear, and the colour would stop meaning anything.
 * `tests/offers-list.test.tsx` pins that against the real default rather than this number.
 */
export const COUNTDOWN_WINDOW_DAYS = 7;

/**
 * The one phrasing of a lapsed Accept-by date, so the `expired` badge and the defensive branch in
 * `out-for-signature` cannot become two ways of saying it. It is the wording the list already used
 * before `expired` was a group, and the wording `OfferStageBadge` uses on the record itself.
 */
export const PAST_DEADLINE_LABEL = 'Past the accept by date';

export function statusBadge(row: OfferRow): { readonly label: string; readonly tone: BadgeTone } {
  switch (row.group) {
    case 'to-prepare':
      return { label: 'Not started', tone: 'neutral' };
    case 'drafts':
      return { label: 'Draft', tone: 'neutral' };
    case 'ready-to-send':
      return { label: 'Ready to send', tone: 'neutral' };
    case 'signed':
      return { label: 'Signed', tone: 'good' };
    case 'expired':
      return { label: PAST_DEADLINE_LABEL, tone: 'warning' };
    case 'out-for-signature': {
      const days = row.dueInDays;
      if (days === null) return { label: 'Sent', tone: 'brand' };
      /*
       * DEFENSIVE since #301, not the live path. An offer whose deadline has passed is now `expired`,
       * and this count cannot be negative in this group: it counts agency calendar days, and a
       * deadline whose agency day is behind `at` is behind `at` as an instant too, which is what
       * `offerExpired` tests. `tests/offers-api.test.ts` pins that no row the API builds reaches here.
       * It stays because a negative number must never render as "Due in -1 days" — a response from an
       * older API, or a hand-built row, would otherwise say exactly that.
       */
      if (days < 0) return { label: PAST_DEADLINE_LABEL, tone: 'warning' };
      if (days === 0) return { label: 'Due today', tone: 'warning' };
      if (days < COUNTDOWN_WINDOW_DAYS) return { label: `Due in ${days} ${days === 1 ? 'day' : 'days'}`, tone: 'warning' };
      return { label: 'Sent', tone: 'brand' };
    }
  }
}

/** The two lines in the Key date column; `null` when the row has no date worth showing. */
export function keyDate(row: OfferRow): { readonly primary: string; readonly secondary: string | null } | null {
  switch (row.group) {
    case 'to-prepare':
      return null;
    case 'drafts':
    case 'ready-to-send':
      return row.draftedAt === null ? null : { primary: `Drafted ${day(row.draftedAt)}`, secondary: row.draftedBy === null ? null : `by ${row.draftedBy}` };
    // An expired offer reads the same two dates as one still out — the date it was due and the day it
    // went out. They are what HR needs to decide whether to chase the candidate or write new terms.
    case 'expired':
    case 'out-for-signature':
      return row.offer === null ? null : {
        primary: `Accept by ${dayAndTime(row.offer.acceptanceDeadline)}`,
        secondary: row.issuedAt === null ? null : `Sent ${day(row.issuedAt)}`,
      };
    case 'signed':
      return row.signedAt === null ? null : { primary: `Signed ${day(row.signedAt)}`, secondary: 'Filed to Employee records' };
  }
}

/** What the row's button offers to do. Reading it is always allowed; the action may not be. */
export function rowAction(row: OfferRow): { readonly label: string; readonly adminOnly: boolean } {
  switch (row.group) {
    case 'to-prepare':
      return { label: 'Draft offer', adminOnly: true };
    case 'drafts':
      return { label: 'Review', adminOnly: true };
    case 'ready-to-send':
      return { label: 'Send', adminOnly: true };
    default:
      return { label: 'View', adminOnly: false };
  }
}
